import { useCrmLocale } from '../ui/locale/crmLocale';
/**
 * LogoutButton
 * ------------
 * Unified logout trigger component that:
 *  - Clears client auth state (via `useAuth().clearAuth()`)
 *  - Optionally removes provided local/session storage keys
 *  - Dispatches a global `app:logout` CustomEvent for any listeners (WS hooks, caches)
 *  - Attempts a best‑effort server logout (CSRF‑aware) without blocking navigation
 *  - Closes any tracked WebSocket references stored under `window.__activeSockets`
 *  - Navigates (SPA or full reload) to the desired post‑logout route
 *
 * Security & Resilience:
 *  - CSRF token refresh on 403, single retry.
 *  - Beacon API used (if available) to increase likelihood of server receiving logout on tab close.
 *  - Force reload option for cross‑section redirects to avoid base path issues.
 *
 * Accessibility:
 *  - While request in flight the button sets `aria-busy` and is disabled.
 *  - Consumer can supply custom `label` node; ensure meaningful text is present.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { dispatchLogoutAfterAuthClear } from '../auth/logoutBoundary';
import { resetBootstrap } from '../auth/authBootstrap';
import { resetAuthFetchLogoutFlag } from '../utils/authFetch';
import { withCsrfInit, ensureCsrfToken } from '../csrf';
import { resolveEmployeeLoginPathFromUser } from '../pages/employee/employeeAuthPaths';

import { fetchSessionMeResponse, postLogoutResponse } from '../api/auth';

export interface LogoutButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  endpoint?: string; // e.g. /api/logout (server should clear session cookie)
  redirectTo?: string; // default '/' (override for role-specific logout targets)
  clearKeys?: string[]; // localStorage/sessionStorage keys to remove
  label?: React.ReactNode;
  iconClassName?: string;
  forceReload?: boolean; // if true, use window.location.replace instead of SPA navigate after clearing auth
}

export const LogoutButton: React.FC<LogoutButtonProps> = ({
  endpoint = '/api/logout',
  redirectTo = '/',
  clearKeys = [],
  label = 'Logout',
  iconClassName = 'fas fa-sign-out-alt mr-2 opacity-80',
  forceReload = false,
  className,
  ...rest
}) => {
  const { t: crmText } = useCrmLocale();

  const navigate = useNavigate();
  const { clearAuth } = useAuth();
  const [loading, setLoading] = React.useState(false);

  const awaitWithTimeout = React.useCallback(async <T,>(p: Promise<T>, ms: number): Promise<T | null> => {
    let timeoutId: number | undefined;
    try {
      const timeout = new Promise<null>((resolve) => {
        timeoutId = window.setTimeout(() => resolve(null), ms);
      });
      return (await Promise.race([p, timeout])) as T | null;
    } finally {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    }
  }, []);
  const clearClientArtifacts = React.useCallback((keys: string[]) => {
    for (const k of keys) {
      try { localStorage.removeItem(k); } catch { /* ignore */ }
      try { sessionStorage.removeItem(k); } catch { /* ignore */ }
    }
    try {
      const sockets = window.__activeSockets;
      sockets?.forEach(s => { try { s.close(); } catch { /* ignore */ } });
      window.__activeSockets = [];
    } catch { /* ignore */ }
  }, []);

  const sendLogout = React.useCallback(async () => {
    const candidates = [...new Set([endpoint, '/api/logout', '/logout'])];

    const attempt = async (targetEndpoint: string): Promise<Response | null> => {
      const runAttempt = async () => {
        const init = await withCsrfInit({ method: 'POST', credentials: 'include', keepalive: true });
        return postLogoutResponse(targetEndpoint, init);
      };
      try {
        let resp = await runAttempt();
        if (resp.status === 403) {
          await ensureCsrfToken(true);
          resp = await runAttempt();
        }
        return resp;
      } catch {
        return null;
      }
    };

    for (const target of candidates) {
      const resp = await attempt(target);
      if (!resp) continue;
      if (resp.ok) return;
      // Treat unauthorized as effectively logged out from client perspective.
      if (resp.status === 401 || resp.status === 403) return;
      // If endpoint is missing or method mismatch, try unified fallback endpoints.
      if (resp.status === 404 || resp.status === 405) continue;
    }

    // Last-chance best effort.
    if (navigator.sendBeacon) {
      try { navigator.sendBeacon('/api/logout', ''); } catch { /* ignore */ }
      if (endpoint !== '/api/logout') {
        try { navigator.sendBeacon(endpoint, ''); } catch { /* ignore */ }
      }
    }
  }, [endpoint]);

  const onClick = React.useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (loading) return; // in-flight guard
    if (rest.onClick) rest.onClick(e);
    if (e.defaultPrevented) return;
    setLoading(true);
    const employeeLoginPath = resolveEmployeeLoginPathFromUser();
    const effectiveRedirect = redirectTo === '/employee/login'
      ? employeeLoginPath
      : (redirectTo === '/' && endpoint.includes('/reception/logout'))
        ? employeeLoginPath
        : redirectTo;
    // Purge auth/navigation before cache listeners observe the global logout event.
    dispatchLogoutAfterAuthClear(clearAuth, { reason: 'manual', endpoint });
    clearClientArtifacts(clearKeys);
    resetBootstrap();
    resetAuthFetchLogoutFlag();
    // Fire and forget network logout
    try { if (navigator.sendBeacon) navigator.sendBeacon(endpoint, ''); } catch { /* ignore */ }
    const logoutPromise = sendLogout().catch(()=>{});
    // For cross-base redirects (e.g., from /manager or /hr to /login), prefer full reload to avoid basename prefixing.
    if (typeof window !== 'undefined') {
      const underAltBase = window.location.pathname.startsWith('/manager') || window.location.pathname.startsWith('/hr');
      const wantsAbsolute = effectiveRedirect.startsWith('/');
      if (forceReload || (underAltBase && wantsAbsolute)) {
        // Critical: when navigating away, browsers may cancel in-flight fetches before
        // applying Set-Cookie (session deletion). Await briefly so logout becomes reliable.
        await awaitWithTimeout(logoutPromise as Promise<unknown>, 1200);
        try { window.location.replace(effectiveRedirect); return; } catch { /* ignore */ }
      }
    }
    navigate(effectiveRedirect, { replace: true });
    // Optionally probe to ensure session is gone (non-blocking):
    setTimeout(async () => {
      try {
        const r = await fetchSessionMeResponse({ credentials: 'include' });
        if (r.ok) {
          /* If still ok, server didn't clear cookie; optionally could force another endpoint */
        }
      } catch {
        /* ignore */
      }
    }, 150);
    setLoading(false); // navigation already triggered
  }, [loading, rest, redirectTo, endpoint, clearKeys, clearAuth, forceReload, navigate, sendLogout, clearClientArtifacts, awaitWithTimeout]);

  return (
    <button {...rest} type="button" onClick={onClick} disabled={loading || rest.disabled} aria-busy={loading || rest['aria-busy']} className={className}>
      <i className={iconClassName} aria-hidden="true" />{label || crmText("Logout")}
    </button>
  );
};

export default LogoutButton;

declare global {
  interface Window { __activeSockets?: WebSocket[] }
}


